import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";

export interface SubCategory {
  id: string;
  name: string;
}

export interface Category {
  id: string;
  name: string;
  description: string;
  icon: string;
  cover: string;
  subcategories: SubCategory[];
}

export interface Article {
  id: string;
  categoryId: string;
  subcategory: string;
  title: string;
  summary: string;
  cover: string;
  author: string;
  publishedAt: string;
  views: number;
  tags: string[];
  content: string;
}

export interface Topic {
  id: string;
  title: string;
  summary: string;
  categoryId: string;
  cover: string;
  date: string;
  hot: number;
}

export const allArticles = articles as Article[];
export const allCategories = categories as Category[];
export const allTopics = topics as Topic[];

export function getCategoryById(categoryId: string | undefined): Category | undefined {
  if (!categoryId) return undefined;
  return allCategories.find((item) => item.id === categoryId);
}

export function getSubCategoryName(categoryId: string, subcategoryId: string): string {
  const category = getCategoryById(categoryId);
  const found = category?.subcategories.find((item) => item.id === subcategoryId);
  return found ? found.name : subcategoryId;
}

export function getArticleById(id: string | undefined): Article | undefined {
  if (!id) return undefined;
  return allArticles.find((item) => item.id === id);
}

export function getArticlesByCategory(categoryId: string): Article[] {
  return allArticles.filter((item) => item.categoryId === categoryId);
}

export function getLatestByCategory(categoryId: string, limit: number): Article[] {
  return getArticlesByCategory(categoryId)
    .slice()
    .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
    .slice(0, limit);
}

export function getHotArticles(limit: number): Article[] {
  return allArticles.slice().sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getRelatedArticles(article: Article, limit = 5): Article[] {
  const sameCategory = allArticles.filter(
    (item) => item.categoryId === article.categoryId && item.id !== article.id
  );
  const sameSub = sameCategory.filter((item) => item.subcategory === article.subcategory);
  const rest = sameCategory.filter((item) => item.subcategory !== article.subcategory);
  return [...sameSub, ...rest].slice(0, limit);
}

export type SortKey = "latest" | "hot";

export function sortArticles(list: Article[], sort: SortKey): Article[] {
  const copy = list.slice();
  if (sort === "hot") {
    return copy.sort((a, b) => b.views - a.views);
  }
  return copy.sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
}

export function filterArticles(options: {
  categoryId?: string;
  subcategory?: string;
  sort?: SortKey;
  dateFrom?: string;
  dateTo?: string;
  query?: string;
}): Article[] {
  const { categoryId, subcategory, sort = "latest", dateFrom, dateTo, query } = options;
  let list = allArticles.slice();

  if (categoryId) {
    list = list.filter((item) => item.categoryId === categoryId);
  }
  if (subcategory && subcategory !== "all") {
    list = list.filter((item) => item.subcategory === subcategory);
  }
  if (dateFrom) {
    list = list.filter((item) => item.publishedAt >= dateFrom);
  }
  if (dateTo) {
    list = list.filter((item) => item.publishedAt <= dateTo);
  }
  if (query && query.trim()) {
    const keyword = query.trim().toLowerCase();
    list = list.filter((item) => {
      const haystack = [item.title, item.summary, item.author, ...item.tags]
        .join(" ")
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }
  return sortArticles(list, sort);
}

export function searchArticles(query: string): Article[] {
  return filterArticles({ query });
}

export interface ContentBlock {
  type: "heading" | "quote" | "list" | "paragraph";
  items?: string[];
  text?: string;
}

export function parseContent(content: string): ContentBlock[] {
  const lines = content.split("\n");
  const blocks: ContentBlock[] = [];
  let listBuffer: string[] = [];

  const flushList = () => {
    if (listBuffer.length) {
      blocks.push({ type: "list", items: listBuffer });
      listBuffer = [];
    }
  };

  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      flushList();
      return;
    }
    if (line.startsWith("## ")) {
      flushList();
      blocks.push({ type: "heading", text: line.slice(3).trim() });
      return;
    }
    if (line.startsWith("> ")) {
      flushList();
      blocks.push({ type: "quote", text: line.slice(2).trim() });
      return;
    }
    if (line.startsWith("- ")) {
      listBuffer.push(line.slice(2).trim());
      return;
    }
    flushList();
    blocks.push({ type: "paragraph", text: line });
  });
  flushList();
  return blocks;
}

export function formatDate(value: string): string {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${year}年${month}月${day}日`;
}

export function formatShortDate(value: string): string {
  if (!value) return "";
  const parts = value.split("-");
  if (parts.length < 3) return value;
  return `${parts[1]}.${parts[2]}`;
}

export function formatViews(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  return `${value}`;
}